import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  TextField,
  Grid,
  Paper,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormLabel,
  makeStyles,
} from "@material-ui/core";
import { orgsService, teamsService, titlesService } from "../services";
import { UserType } from "../services/domain/User";
import { Select } from "@ofqual/portal-components/components/Select";
import { createUser } from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { roles } from "./roleHelper";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { Error } from "../presentation/common/Error";
import { UsersAppBar } from "./UsersAppBar";
import { Save } from "@material-ui/icons";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { authSelector, createUserSelector } from "../redux/selectors";
import {
  UserNewViewModel,
  defaultValues,
  fromViewModel,
} from "./viewModels/UserNewViewModel";
import { validationHelper } from "./common/validationHelper";
import { CreateUserStatus } from "../redux/state";

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const useStyles = makeStyles((theme) => {
  return {
    root: {
      marginTop: theme.contentOffsets.AFTER_SECONDARY_APPBAR,
    },
  };
});

export const UserNew: React.FC = () => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const { userType: loggedInUserType, orgId } = useAuth(authSelector);

  const {
    register,
    handleSubmit,
    errors,
    control,
    watch,
    setValue,
    setError,
  } = useForm<UserNewViewModel>({
    defaultValues,
  });

  const createState = useSelector(createUserSelector);
  const { status, lastCrudResult } = createState;

  const isEditable = status !== "CREATING";

  useEffect(() => {
    validationHelper(lastCrudResult, (name, message) =>
      setError(name, { message })
    );
  }, [lastCrudResult, setError]);

  const onSubmit = (viewModel: UserNewViewModel) => {
    dispatch(
      createUser(
        fromViewModel(
          viewModel,
          // if awardingOrgId is null we are an AO admin, so use the logged-in orgId
          parseInt(viewModel.awardingOrgIdStr, 10) || orgId,
          userType
        )
      )
    );
  };

  const { userTypeStr } = watch(["userTypeStr"], defaultValues);
  const userType = parseInt(userTypeStr, 10) as UserType;

  const roleLookups = roles[userType].roleLookups.map((item) => ({
    ...item,
    disabled: item.isDefault,
  }));

  const [teamLookups, orgsLookups, titlesLookups] = useLookup(
    {
      service: teamsService,
      id: "id",
      label: "name",
    },
    {
      service: orgsService,
      id: "itemId",
      label: "name",
    },
    titlesService
  );

  return (
    <div className={classes.root}>
      <UsersAppBar breadcrumbs={[{ label: "New User" }]}>
        <AppBarSaveButton
          lifecycleLabels={["Create", "Creating", "Created"]}
          isSubmitting={createState.status === CreateUserStatus.creating}
          isSubmitted={createState.lastCrudResult.isSuccess}
          onClick={handleSubmit(onSubmit)}
          disabled={!isEditable}
          Icon={Save}
        />
      </UsersAppBar>
      <Paper style={{ padding: 20 }}>
        <form>
          <Grid container spacing={3}>
            {loggedInUserType === UserType.Ofqual ? (
              <Grid item xs={12}>
                <FormLabel>User Type:</FormLabel>
                <Controller
                  control={control}
                  name="userTypeStr"
                  render={({ onChange, value, name, onBlur }) => {
                    const _onChange = (event: any) => {
                      // we reset the roles field if user type changes
                      //  a) some user types have default roles that the roles field need to be kicked off with
                      //  b) it makes no sense to persist roles for one user type to another
                      setValue(
                        "userRoles",
                        roles[event.target.value as UserType].defaultRoles
                      );
                      onChange(event);
                    };
                    return (
                      <RadioGroup
                        aria-label="userType"
                        onChange={_onChange}
                        onBlur={onBlur}
                        name={name}
                        value={value}
                        data-test="user-type-radio"
                      >
                        <FormControlLabel
                          value={String(UserType.AO)}
                          control={<Radio disabled={!isEditable} />}
                          label={UserType[UserType.AO]}
                          data-test="user-type-radio-ao"
                        />
                        <FormControlLabel
                          value={String(UserType.Ofqual)}
                          control={<Radio disabled={!isEditable} />}
                          label={UserType[UserType.Ofqual]}
                          data-test="user-type-radio-ofqual"
                        />
                        <FormControlLabel
                          value={String(UserType.CCEA)}
                          control={<Radio disabled={!isEditable} />}
                          label={`${UserType[UserType.CCEA]} Regulation`}
                          data-test="user-type-radio-ccea"
                        />
                      </RadioGroup>
                    );
                  }}
                />
              </Grid>
            ) : null}

            {userType === UserType.AO &&
            loggedInUserType === UserType.Ofqual ? (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="awardingOrgIdStr"
                  rules={{ required: true }}
                  as={
                    <Select
                      label="Awarding Org"
                      disabled={!isEditable}
                      data-test="ao-select"
                    >
                      {orgsLookups}
                    </Select>
                  }
                />

                {errors.awardingOrgIdStr && (
                  <Error>This field is required</Error>
                )}
              </Grid>
            ) : null}

            {userType === UserType.AO || userType === UserType.CCEA ? (
              <React.Fragment>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="title"
                    rules={{ required: true }}
                    as={
                      <Select
                        label="Title"
                        disabled={!isEditable}
                        data-test="user-title"
                      >
                        {titlesLookups}
                      </Select>
                    }
                  />
                  {errors.title && <Error>This field is required</Error>}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="firstName"
                    inputRef={register({ required: true })}
                    variant="outlined"
                    label="First name"
                    fullWidth
                    disabled={!isEditable}
                    data-test="user-first-name"
                  />
                  {errors.firstName && <Error>This field is required</Error>}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="surname"
                    inputRef={register({ required: true })}
                    variant="outlined"
                    label="Surname"
                    fullWidth
                    disabled={!isEditable}
                    data-test="user-surname"
                  />
                  {errors.surname && <Error>This field is required</Error>}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="alternateEmail"
                    inputRef={register({
                      required: true,
                      pattern: EMAIL_PATTERN,
                    })}
                    variant="outlined"
                    label="Email"
                    fullWidth
                    disabled={!isEditable}
                    data-test="user-alt-email"
                  />
                  {errors.alternateEmail && (
                    <Error>
                      {!!errors.alternateEmail.message
                        ? errors.alternateEmail.message
                        : "Please enter a valid email"}
                    </Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="telephoneNumber"
                    inputRef={register({ required: true })}
                    variant="outlined"
                    label="Phone number"
                    fullWidth
                    disabled={!isEditable}
                    data-test="user-tel-number"
                  />
                  {errors.telephoneNumber && (
                    <Error>This field is required</Error>
                  )}
                </Grid>
              </React.Fragment>
            ) : null}

            {userType === UserType.Ofqual ? (
              <React.Fragment>
                <Grid item xs={12}>
                  <TextField
                    name="upn"
                    inputRef={register({
                      required: true,
                      pattern: EMAIL_PATTERN,
                    })}
                    variant="outlined"
                    label="Email"
                    fullWidth
                    disabled={!isEditable}
                    data-test="user-email"
                  />
                  {errors.upn && (
                    <Error>
                      {!!errors.upn.message
                        ? errors.upn.message
                        : "Please enter a valid email"}
                    </Error>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="teamIds"
                    rules={{ validate: (value) => !!value.length }}
                    as={
                      <Select
                        selectVariant="multiple"
                        label="Team(s)"
                        data-test="user-teams"
                      >
                        {teamLookups}
                      </Select>
                    }
                  />
                  {errors.teamIds && <Error>This field is required</Error>}
                </Grid>
              </React.Fragment>
            ) : null}

            <Grid item xs={12}>
              <Controller
                control={control}
                name="userRoles"
                rules={{ validate: (value) => !!value.length }}
                as={
                  <Select
                    selectVariant="multiple"
                    label="User Role(s)"
                    data-test="user-roles"
                  >
                    {roleLookups}
                  </Select>
                }
              />
              {errors.userRoles && <Error>This field is required</Error>}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};
